import { useMediaQuery } from '@mantine/hooks'
import { Modal, Drawer, FocusTrap, Loader } from '@mantine/core'
import { Typography } from '../../common/typography'
import classes from './Breakdown.module.css'
import RatingBreakdown from './RatingBreakdown'
import TotalPointsBreakdown from './TotalPointsBreakdown'
import { usePlayerBreakdown, useTeamBreakdown } from './api'

interface BreakdownProps {
  opened: boolean
  close: () => void
  competitionId: number
  playerId?: number
  teamId?: number
  subject: 'rating' | 'total-points'
}

export default function Breakdown({
  opened,
  close,
  competitionId,
  playerId,
  teamId,
  subject,
}: BreakdownProps) {
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const playerBreakdown = usePlayerBreakdown({
    competition_id: competitionId,
    player_id: playerId,
    enabled: opened,
  })

  const teamBreakdown = useTeamBreakdown({
    competition_id: competitionId,
    team_id: teamId,
    enabled: opened,
  })

  const { data, isLoading } = playerId ? playerBreakdown : teamBreakdown

  return (
    <>
      {isDesktop ? (
        <Modal.Root
          opened={opened}
          onClose={close}
          centered
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{ header: classes.header, body: classes.body }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <FocusTrap.InitialFocus />
            <Modal.Header>
              <Modal.Title>
                <Typography fs="xl" fw={700} lh="120">
                  {subject === 'rating'
                    ? 'Rating Breakdown'
                    : 'Total Points Breakdown'}
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              {isLoading ? (
                <Loader />
              ) : !data ? (
                <Typography>Currently there is no data available</Typography>
              ) : subject === 'rating' ? (
                <RatingBreakdown data={data} />
              ) : (
                <TotalPointsBreakdown data={data} />
              )}
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={opened}
          onClose={close}
          position="bottom"
          classNames={{
            header: classes.header,
            body: classes.body,
            content: classes.content,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <FocusTrap.InitialFocus />
            <Drawer.Header>
              <Drawer.Title>
                <Typography fs="xl" fw={700} lh="120">
                  {subject === 'rating'
                    ? 'Rating Breakdown'
                    : 'Total Points Breakdown'}
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              {isLoading ? (
                <Loader />
              ) : !data ? (
                <Typography>Currently there is no data available</Typography>
              ) : subject === 'rating' ? (
                <RatingBreakdown data={data} />
              ) : (
                <TotalPointsBreakdown data={data} />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </>
  )
}
